import { Chain } from '@portafino/shared'
import { Address } from 'viem'
import { z } from 'zod'

const AddressRegex = /^0x[a-fA-F0-9]{40}$/

interface Env {
  rpcUrl: string
  bundlerUrl: string
  gianoPaymasterAddress?: Address
  gianoSmartWalletFactoryAddress: Address
  chain: Chain
  sentryDsn?: string
  sentryRelease?: string
  dev: boolean
  env: string
  mode: string
  prod: boolean
  explorerUrl?: string
  tokenAddress: Address
  bridgeUrl: string
}

const buildEnv = (env: ImportMetaEnv) => {
  return {
    rpcUrl: env.VITE_RPC_URL,
    bundlerUrl: env.VITE_BUNDLER_URL,
    gianoPaymasterAddress: env.VITE_GIANO_PAYMASTER_ADDRESS,
    gianoSmartWalletFactoryAddress: env.VITE_GIANO_SMART_WALLET_FACTORY_ADDRESS,
    chain: env.VITE_PUBLIC_CHAIN,
    sentryDsn: env.VITE_SENTRY_DSN,
    sentryRelease: env.VITE_SENTRY_RELEASE,
    dev: env.DEV,
    env: env.VITE_ENV,
    mode: env.MODE,
    prod: env.PROD,
    explorerUrl: env.VITE_EXPLORER_URL,
    tokenAddress: env.VITE_TOKEN_ADDRESS,
    bridgeUrl: env.VITE_BRIDGE_URL,
  }
}

const envSchema = z.object({
  rpcUrl: z.string(),
  bundlerUrl: z.string(),
  gianoPaymasterAddress: z
    .string()
    .optional()
    .refine((data) => !data || AddressRegex.test(data), {
      message: 'Invalid address',
    })
    .transform((data) => data as Address),
  gianoSmartWalletFactoryAddress: z
    .string()
    .regex(AddressRegex)
    .transform((data) => data as Address),
  chain: z.nativeEnum(Chain),
  sentryDsn: z.string().optional(),
  sentryRelease: z.string().optional(),
  dev: z.boolean(),
  env: z.string(),
  mode: z.string(),
  prod: z.boolean(),
  explorerUrl: z.string().optional().default('https://basescan.org'),
  tokenAddress: z
    .string()
    .regex(AddressRegex)
    .transform((data) => data as Address),
  bridgeUrl: z.string().url(),
})

const validateEnv = (env: ImportMetaEnv) => {
  const envVariables = buildEnv(env)
  const result = envSchema.safeParse(envVariables)
  if (!result.success) {
    throw new Error(`Invalid environment variables: ${result.error.message}`)
  }
  const envValues: Env = result.data
  return envValues
}

export const env = validateEnv(import.meta.env)
